<template>
  <article
    class="chit"
    :class="{
      'chit--selected': isSelected,
      'chit--urgent': isUrgent && !isSelected,
      'chit--ready': isReadyTab && !isSelected,
    }"
    :style="chitColorStyle"
    @click="$emit('select', order)"
  >
    <header class="chit__header">
      <div class="chit__id-block">
        <span class="chit__order-label">{{ t.order }}</span>
        <div class="chit__receipt">{{ displayReceipt }}</div>
      </div>
      <div class="chit__meta">
        <div v-if="showOpenTime || order.paid_at_time" class="chit__times">
          <!-- Worded labels, not icons: read at a glance from a distance. -->
          <span v-if="showOpenTime" class="chit__time-pill">
            <span class="chit__time-label">{{ t.opened }}:</span>{{ order.created_at_time }}
          </span>
          <span v-if="order.paid_at_time" class="chit__time-pill">
            <span class="chit__time-label">{{ t.paid }}:</span>{{ order.paid_at_time }}
          </span>
        </div>
        <div>
          <span class="chit__platform-badge">{{ platformLabel }}</span>
          <span
            class="chit__status-badge mx-1"
            :class="{ 'chit__status-badge--alert': isOrderStatusAlert }"
            :title="t.orderStatus"
          >
            <span class="chit__status-badge-label">{{ t.orderStatusShort }}</span>{{ order.pos_status_label }}
          </span>
          <span class="chit__status-badge chit__status-badge--kitchen" :title="t.kitchenStatus">
            <span class="chit__status-badge-label">{{ t.kitchenStatusShort }}</span>{{ kitchenStatusLabel }}
          </span>
        </div>
        <div v-if="diningLabel || tableNo" class="chit__place">
          <!-- M1025: tells the kitchen which packaging the order goes out in. -->
          <span
            v-if="diningLabel"
            class="chit__dining-badge"
            :class="{ 'chit__dining-badge--takeaway': isTakeaway }"
          >
            <i class="material-icons-round chit__dining-icon">{{
              isTakeaway ? "takeout_dining" : "restaurant"
            }}</i>{{ diningLabel }}
          </span>
          <span v-if="tableNo" class="chit__table-badge">{{ t.table }}: {{ tableNo }}</span>
        </div>
      </div>
    </header>

    <div class="chit__items">
      <OrderChitItem
        v-for="(item, index) in items"
        :key="index"
        :item="item"
        :name-field="nameField"
        :show-description="showDescription"
        :description-field="descriptionField"
        :struck-keys="struckKeys"
        :strikeable="tab === ORDER_STATUS.PREPARING"
        @strike="$emit('strike', order, $event)"
      />
      <div v-if="hasNote" class="chit__note">{{ order.note }}</div>
    </div>

    <div class="chit__footer" @click.stop>
      <button
        v-if="tab === ORDER_STATUS.PREPARING"
        type="button"
        class="chit__btn chit__btn--done"
        :class="{ 'chit__btn--urgent': isUrgent }"
        @click="$emit('done', order)"
      >
        <span class="chit__btn-left">
          {{ t.done }}
        </span>
        <span class="chit__btn-right" :class="{ 'chit__btn-right--pulse': isUrgent }">
          <i class="material-icons-round chit__btn-icon">
            {{ isUrgent ? 'hourglass_bottom' : 'schedule' }}
          </i>
          {{ elapsedDisplay }}
        </span>
      </button>
      <div
        v-else-if="tab === ORDER_STATUS.READY"
        class="chit__btn-split"
      >
        <button
          type="button"
          class="chit__btn-side chit__btn-side--recall"
          @click="$emit('recall', order)"
        >
          <i class="material-icons-round chit__btn-icon">undo</i>
          {{ t.recall }}
        </button>
        <!-- Collect hands over the whole order, so it waits until every
             station has pressed Done. -->
        <button
          type="button"
          class="chit__btn-side chit__btn-side--collect"
          :disabled="!isKitchenReady"
          :title="isKitchenReady ? '' : t.waitingTitle"
          @click="$emit('collect', order)"
        >
          <i class="material-icons-round chit__btn-icon">
            {{ isKitchenReady ? 'shopping_bag' : 'hourglass_empty' }}
          </i>
          {{ isKitchenReady ? t.collect : t.waiting }}
        </button>
      </div>
      <!-- Pending tab: view-only, no action button (kitchen accepts an order
           by physically starting prep; status transitions happen on payment). -->
    </div>
  </article>
</template>

<script>
import OrderChitItem from "./OrderChitItem.vue";
import { ORDER_STATUS } from "../mock_data";
import {
  parseCreatedAt,
  formatElapsed,
  isOrderKitchenReady,
} from "../kds.utils";

const DINING_LABEL_TAKEAWAY = "Takeaway";

const PLATFORM_LABELS = {
  1: "POS",
  2: "QR",
  3: "Kiosk",
  4: "Grab",
};

// Chit chrome. Always English — the KDS Language setting picks which item
// name column the chit prints, it is not a UI locale (M939).
const CHIT_LABELS = {
  order: "ORDER",
  table: "Table",
  done: "DONE",
  recall: "RECALL",
  collect: "COLLECT",
  orderStatus: "Order status",
  orderStatusShort: "Order",
  kitchenStatus: "Kitchen status",
  kitchenStatusShort: "Kitchen",
  preparing: "Preparing",
  ready: "Ready",
  opened: "OPEN",
  paid: "PAID",
  partlyReady: "Partly ready",
  waiting: "WAITING",
  waitingTitle: "Other stations are still preparing this order",
};

// POS status values the BE normalises is_pushed_to_megapos_database to
// (MintyTransaction::UNPAID / PUSH_FAILED). These two mean the order needs
// someone's attention before the kitchen can rely on it, so the badge turns
// red rather than reading like any other state.
const POS_STATUS_UNPAID = -1;
const POS_STATUS_PUSH_FAILED = 3;
const POS_STATUS_ALERT = [POS_STATUS_UNPAID, POS_STATUS_PUSH_FAILED];

export default {
  name: "OrderChit",
  components: {
    OrderChitItem,
  },
  props: {
    order: {
      type: Object,
      required: true,
    },
    items: {
      type: Array,
      default: () => [],
    },
    tab: {
      type: Number,
      required: true,
    },
    // Current "wall clock" in ms — broadcast from KdsManagement so all chits
    // tick in lockstep with a single setInterval rather than each card running
    // its own timer.
    now: {
      type: Number,
      required: true,
    },
    // Alert threshold in milliseconds. Cards whose elapsed time exceeds it
    // flip to the "urgent" red style (unless the card is currently selected,
    // in which case the black selected style wins by design).
    alertAfterMs: {
      type: Number,
      default: 5 * 60 * 1000,
    },
    // Merchant-configured colour for the urgent state. Exposed as a CSS
    // variable so all four `--urgent` selectors (header bg, time-pill bg,
    // badge border/colour, done button bg) update in one place.
    alertColor: {
      type: String,
      default: "#ef4444",
    },
    // Merchant-configured colour for a chit on the READY tab (M945). Ready
    // orders are always past the alert threshold, so painting them with the
    // alert colour made every card red and drowned the real alerts.
    readyColor: {
      type: String,
      default: "#3b82f6",
    },
    // KDS Language setting — which item name column to print:
    // "name1" (Item Name) | "name2" | "name3" | "name4".
    nameField: {
      type: String,
      default: "name1",
    },
    // KDS Display-both toggle: print a second line under the name.
    showDescription: {
      type: Boolean,
      default: false,
    },
    // Which column feeds that second line: "none" = the item's own
    // Description, otherwise a name column (M942).
    descriptionField: {
      type: String,
      default: "none",
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    // M985: line keys the station on screen has struck out.
    struckKeys: {
      type: Array,
      default: () => [],
    },
    // KDS "Display open time" setting.
    showOpenTime: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["done", "recall", "collect", "select", "strike"],
  data() {
    return {
      ORDER_STATUS,
    };
  },
  computed: {
    receiptNo() {
      return String(this.order.receipt_no ?? "");
    },
    displayReceipt() {
      if (this.order.platform_type === 4) return this.receiptNo;
      return this.receiptNo.length >= 3
        ? this.receiptNo.slice(-3)
        : this.receiptNo;
    },
    tableNo() {
      const t = this.order.table_no;
      return t === null || t === undefined || t === "" ? "" : String(t);
    },
    // M1025: "Takeaway" / "Dine-in" from the BE; null for bills keyed in at the POS.
    diningLabel() {
      return this.order.dining_option_label || "";
    },
    isTakeaway() {
      return this.diningLabel === DINING_LABEL_TAKEAWAY;
    },
    platformLabel() {
      return (
        PLATFORM_LABELS[this.order.platform_type] ??
        this.order.platform_type_label ??
        ""
      );
    },
    // Ready only once every station is done (see moveOrderKDS).
    isKitchenReady() {
      return isOrderKitchenReady(this.order);
    },
    kitchenStatusLabel() {
      if (this.isKitchenReady) return this.t.ready;
      return this.order.ready_printer_ids?.length
        ? this.t.partlyReady
        : this.t.preparing;
    },
    isOrderStatusAlert() {
      return POS_STATUS_ALERT.includes(
        Number(this.order.is_pushed_to_megapos_database)
      );
    },
    hasNote() {
      const note = this.order.note;
      return note !== null && note !== undefined && String(note).trim() !== "";
    },
    createdAtMs() {
      return parseCreatedAt(this.order);
    },
    elapsedMs() {
      if (this.createdAtMs == null) return 0;
      const diff = this.now - this.createdAtMs;
      // TODO debug: remove once timer is verified across timezones / fallbacks.
      // eslint-disable-next-line no-console
      console.log("[KDS elapsed]", {
        receipt: this.order.receipt_no,
        now: this.now,
        nowIso: new Date(this.now).toISOString(),
        createdAtRaw: this.order.created_at,
        createdAtTime: this.order.created_at_time,
        createdAtMs: this.createdAtMs,
        createdAtIso: new Date(this.createdAtMs).toISOString(),
        diffMs: diff,
        diffMin: Math.round(diff / 60000),
      });
      return Math.max(0, diff);
    },
    elapsedDisplay() {
      return formatElapsed(this.elapsedMs);
    },
    isReadyTab() {
      return this.tab === ORDER_STATUS.READY;
    },
    isUrgent() {
      // The alert style belongs to PREPARING only: a READY chit has already
      // been cooked, so its elapsed time is no longer something to chase
      // (M945). It gets the merchant's Ready colour instead.
      if (this.isReadyTab) return false;
      if (this.createdAtMs == null) return false;
      return this.elapsedMs >= this.alertAfterMs;
    },
    chitColorStyle() {
      return {
        "--chit-alert-color": this.alertColor,
        "--chit-ready-color": this.readyColor,
      };
    },
    t() {
      return CHIT_LABELS;
    },
  },
};
</script>

<style scoped>
/* ---------- Card shell ---------- */
.chit {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #000;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  cursor: pointer;
  transition: box-shadow 0.15s ease;
}

.chit:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

/* ---------- Header (3 colour states) ---------- */
.chit__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px;
  border-bottom: 1px solid #000;
  background: #e0e3e5; /* default = normal */
  color: #191c1e;
}

.chit--urgent .chit__header {
  background: var(--chit-alert-color, #ef4444);
  color: #fff;
}

/* READY tab: merchant-defined colour, never the alert red. Mutually
   exclusive with .chit--urgent (isUrgent is false on that tab), so the two
   equal-specificity blocks can never fight over the same card. */
.chit--ready .chit__header {
  background: var(--chit-ready-color, #3b82f6);
  color: #fff;
}

.chit--selected .chit__header {
  background: #0f172a;
  color: #fff;
}

.chit__order-label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  opacity: 0.8;
}

.chit__receipt {
  font-size: 48px;
  line-height: 52px;
  font-weight: 800;
  letter-spacing: -0.02em;
  margin-top: 2px;
}

.chit__meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  text-align: right;
}

.chit__times {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.chit__time-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 18px;
  font-weight: 600;
  line-height: 22px;
  padding: 2px 8px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.06);
}

.chit--urgent .chit__time-pill {
  background: color-mix(in srgb, var(--chit-alert-color, #ef4444) 55%, #000);
  color: #fff;
}

.chit--ready .chit__time-pill {
  background: color-mix(in srgb, var(--chit-ready-color, #3b82f6) 55%, #000);
  color: #fff;
}

.chit--selected .chit__time-pill {
  background: #1a1a1a;
  color: #fff;
}

.chit__time-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  opacity: 0.75;
}

.chit__platform-badge,
.chit__status-badge,
.chit__table-badge,
.chit__dining-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid #c6c6cd;
  background: #f7f9fb;
  color: #191c1e;
}

.chit--urgent .chit__platform-badge,
.chit--urgent .chit__status-badge,
.chit--urgent .chit__table-badge,
.chit--urgent .chit__dining-badge {
  background: #fff;
  border-color: var(--chit-alert-color, #ef4444);
  color: var(--chit-alert-color, #ef4444);
}

.chit--ready .chit__platform-badge,
.chit--ready .chit__status-badge,
.chit--ready .chit__table-badge,
.chit--ready .chit__dining-badge {
  background: #fff;
  border-color: var(--chit-ready-color, #3b82f6);
  color: var(--chit-ready-color, #3b82f6);
}

/* Kitchen-status badge sits alongside the order-status badge — same shape,
   a distinct fill so the two tags stay visually separable. */
.chit__status-badge--kitchen {
  background: #eef2ff;
  border-color: #c7d2fe;
  color: #3730a3;
}

/* Dim, uppercase caption in front of the badge value ("ORDER Inserted" /
   "KITCHEN Preparing") so the two badges never rely on color alone to tell
   them apart. */
.chit__status-badge-label {
  opacity: 0.65;
  font-weight: 600;
}

.chit__status-badge-label::after {
  content: ":";
  margin-right: 2px;
}

.chit--selected .chit__platform-badge,
.chit--selected .chit__status-badge,
.chit--selected .chit__table-badge,
.chit--selected .chit__dining-badge {
  background: rgba(255, 255, 255, 0.92);
  border-color: rgba(255, 255, 255, 0.4);
  color: #0f172a;
}

/* M1025: dining option sits on its own row with the table, larger than the
   other tags - the packer reads it from across the pass. */
.chit__place {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 6px;
}

.chit .chit__dining-badge {
  font-size: 15px;
  font-weight: 800;
  text-transform: uppercase;
  padding: 3px 10px;
}

.chit__dining-icon {
  font-size: 18px;
}

/* Takeaway keeps its solid fill in every chit state (urgent / ready /
   selected), the same way the alert badge below does: it changes what the
   kitchen does with the order, so it must never fade into the chit colour. */
.chit .chit__dining-badge--takeaway,
.chit--selected .chit__dining-badge--takeaway {
  background: #c2410c;
  border-color: #c2410c;
  color: #fff;
}

/* Failed / Unpaid has to read as a problem whatever else the chit is doing,
   so these sit after the urgent/ready/selected overrides above and match
   their specificity. Deliberately not --chit-alert-color: that one is the
   merchant's configurable "running late" colour, and a bill that failed to
   push is a different problem from a slow order. */
.chit .chit__status-badge--alert,
.chit--selected .chit__status-badge--alert {
  background: #fdeded;
  border-color: #dc3545;
  color: #b02a37;
}

/* ---------- Items ---------- */
.chit__items {
  flex: 1 1 auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Divider between consecutive item rows. Each OrderChitItem renders either a
   plain `.chit-item` (regular item) or a `.chit-item-choice-group` (Set with
   Choice items) as its root — list all four sibling pairs so the divider
   applies regardless of which wrapper class is on either side. */
.chit__items > :deep(.chit-item) + :deep(.chit-item),
.chit__items > :deep(.chit-item) + :deep(.chit-item-choice-group),
.chit__items > :deep(.chit-item-choice-group) + :deep(.chit-item),
.chit__items > :deep(.chit-item-choice-group) + :deep(.chit-item-choice-group) {
  padding-top: 16px;
  border-top: 2px solid #e6e8ea;
}

.chit__note {
  align-self: center;
  display: inline-block;
  width: fit-content;
  padding: 4px 12px;
  border-radius: 9999px;
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

/* ---------- Footer ---------- */
.chit__footer {
  padding: 16px;
  background: #fff;
  border-top: 1px solid #e6e8ea;
}

.chit__btn {
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  border: none;
  border-radius: 9999px;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.03em;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
  transition: opacity 0.15s ease, box-shadow 0.15s ease;
}

.chit__btn:hover {
  opacity: 0.92;
}

.chit__btn:active {
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.chit__btn--done {
  background: #3b82f6;
}

.chit__btn--done.chit__btn--urgent {
  background: var(--chit-alert-color, #ef4444);
}

.chit__btn--recall {
  background: #f5871f;
}

/* READY tab renders Recall + Collect as two independent rounded pills sitting
   side-by-side with a gap between them (Collect replaces the elapsed countdown
   that lived in chit__btn-right). min-width:0 lets each pill shrink under its
   intrinsic content width so the row never overflows the chit footer on narrow
   cards. */
.chit__btn-split {
  width: 100%;
  display: flex;
  gap: 12px;
}

.chit__btn-side {
  flex: 1 1 0;
  min-width: 0;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 16px;
  border: none;
  border-radius: 9999px;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.03em;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
  transition: opacity 0.15s ease, box-shadow 0.15s ease;
}

.chit__btn-side:hover {
  opacity: 0.92;
}

.chit__btn-side:active {
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.chit__btn-side--recall {
  background: #f5871f;
}

.chit__btn-side--collect {
  background: #22c55e;
}

.chit__btn-side:disabled {
  background: #94a3b8;
  cursor: not-allowed;
  opacity: 1;
}

.chit__btn-left,
.chit__btn-right {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.chit__btn-right {
  font-size: 16px;
  opacity: 0.9;
}

.chit__btn-right--pulse {
  animation: chit-pulse 1.2s ease-in-out infinite;
  font-weight: 900;
  opacity: 1;
}

.chit__btn-icon {
  font-size: 18px;
}

@keyframes chit-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.55; }
}
</style>
